// The contract between atzmai-agent and the portal. Every field is on an
// allow-list: unknown events AND unknown fields are rejected, so nothing
// leaks in by accident. Change this file only together with the agent.
import { z } from "zod";

export const SCHEMA_VERSION = 1;
export const MAX_BATCH = 100;
export const MAX_PAYLOAD_BYTES = 1024;

export const releaseVersion = z.string().regex(/^\d{4}\.\d{2}\.\d+$/, "release must look like 2026.10.1");
export const channel = z.enum(["stable", "testers"]);
export const deviceId = z.uuid();

const pct = z.number().int().min(0).max(100);
const reasonCode = z.string().regex(/^[a-z0-9_]{1,48}$/);
const shortText = z.string().max(64);

const payloads = {
  "install.first_boot": z.strictObject({
    model: shortText,
    arch: z.enum(["amd64", "arm64"]),
    source: z.enum(["usb", "net", "preload"]),
    installer: shortText,
    locale: z.string().regex(/^[a-z]{2,3}(_[A-Z]{2})?$/),
  }),
  "session.heartbeat": z.strictObject({
    uptime_h: z.number().int().min(0).max(100_000),
    disk_free_pct: pct,
    power: z.enum(["ac", "battery", "solar"]),
    net: z.enum(["up", "down"]),
    pending_updates: z.number().int().min(0).max(10_000),
  }),
  "update.check": z.strictObject({ current: releaseVersion, available: releaseVersion.nullable() }),
  "update.download": z.strictObject({
    from: releaseVersion, to: releaseVersion,
    bytes: z.number().int().min(0), seconds: z.number().int().min(0),
    mirror: shortText, resumed: z.boolean(), delta: z.boolean(),
  }),
  "update.apply_start": z.strictObject({
    from: releaseVersion, to: releaseVersion, method: z.enum(["apt", "ab"]), free_space: pct,
  }),
  "update.apply_ok": z.strictObject({ from: releaseVersion, to: releaseVersion, seconds: z.number().int().min(0) }),
  "update.apply_fail": z.strictObject({
    from: releaseVersion, to: releaseVersion,
    stage: z.enum(["download", "verify", "unpack", "configure", "boot"]), reason_code: reasonCode,
  }),
  "update.rollback": z.strictObject({ from: releaseVersion, to: releaseVersion, reason_code: reasonCode }),
  "boot.ok": z.strictObject({ boot_count: z.number().int().min(0), last_release: releaseVersion, watchdog: z.boolean() }),
  "boot.fail": z.strictObject({ boot_count: z.number().int().min(0), last_release: releaseVersion, watchdog: z.boolean() }),
  crash: z.strictObject({
    signature: z.string().regex(/^[0-9a-f]{16,64}$/), // stack hash, never a stack
    package: shortText, package_version: shortText, count: z.number().int().min(1).max(10_000),
  }),
  "feature.use": z.strictObject({ feature_id: z.string().regex(/^[a-z0-9_.]{1,48}$/), count: z.number().int().min(0).max(100_000) }),
  "offline.import": z.strictObject({
    bundle_id: shortText, release: releaseVersion, method: z.enum(["usb", "lan"]), count: z.number().int().min(1),
  }),
  feedback: z.strictObject({
    rating: z.number().int().min(1).max(5),
    category: z.enum(["updates", "performance", "children", "hardware", "ai_answers", "other"]),
    message: z.string().max(2000), // the only free text in the schema
    contact_ok: z.boolean(),
  }),
} as const;

export type EventType = keyof typeof payloads;
export const EVENT_TYPES = Object.keys(payloads) as EventType[];
/** Each event's fields, as the server accepts them: the transparency page lists these. */
export const EVENT_FIELDS = Object.fromEntries(
  EVENT_TYPES.map((t) => [t, Object.keys(payloads[t].shape)]),
) as Record<EventType, string[]>;

const envelope = <T extends EventType>(type: T) =>
  z.strictObject({
    schema: z.literal(SCHEMA_VERSION),
    id: z.uuid(),
    device: deviceId,
    ts: z.iso.datetime(),
    type: z.literal(type),
    release: releaseVersion,
    channel,
    payload: payloads[type],
  });

export const deviceEvent = z.discriminatedUnion(
  "type",
  EVENT_TYPES.map((t) => envelope(t)) as unknown as [ReturnType<typeof envelope>, ...ReturnType<typeof envelope>[]],
);
export type DeviceEvent = z.infer<typeof deviceEvent>;

export const eventBatch = z
  .array(deviceEvent)
  .min(1)
  .max(MAX_BATCH)
  .superRefine((events, ctx) => {
    for (const [i, e] of events.entries()) {
      if (JSON.stringify(e.payload).length > MAX_PAYLOAD_BYTES) {
        ctx.addIssue({ code: "custom", path: [i, "payload"], message: `payload over ${MAX_PAYLOAD_BYTES} bytes` });
      }
    }
  });

export const checkinBody = z.strictObject({
  schema: z.literal(SCHEMA_VERSION),
  device: deviceId,
  release: releaseVersion,
  channel,
  first_boot: payloads["install.first_boot"].optional(),
});
export type CheckinBody = z.infer<typeof checkinBody>;

export const feedbackBody = z.strictObject({
  device: deviceId.optional(),
  release: releaseVersion.optional(),
  rating: payloads.feedback.shape.rating,
  category: payloads.feedback.shape.category,
  message: payloads.feedback.shape.message,
  contact_ok: z.boolean().default(false),
});
